export type WorkflowVariableDataType =
  | "string"
  | "number"
  | "boolean"
  | "enum"
  | "duration"
  | "datetime"
  | "date"
  | "identifier"

export type WorkflowValueControl = "text" | "number" | "select" | "boolean" | "duration" | "datetime" | "none"
export type WorkflowJsonValueType = "string" | "number" | "boolean" | "null"

export type WorkflowVariableDefinition = {
  key: string
  label: string
  description: string
  group: string
  dataType: WorkflowVariableDataType
  operators: string[]
  valueControl: WorkflowValueControl
  options?: Array<{ label: string; value: string | number | boolean }>
  optionsSource?: string
  unitOptions?: Array<{ label: string; value: string; multiplierToMinutes?: number }>
  exampleValues?: unknown[]
  jsonValueType: WorkflowJsonValueType
  nullable?: boolean
  advanced?: boolean
  runtimeAvailable?: boolean
  aliases?: string[]
  plainEnglish?: string
}

export type WorkflowOperatorDefinition = {
  key: string
  label: string
  description: string
  dataTypes: WorkflowVariableDataType[]
  requiresValue: boolean
}

export const DURATION_UNIT_OPTIONS = [
  { label: "Minutes", value: "minutes", multiplierToMinutes: 1 },
  { label: "Hours", value: "hours", multiplierToMinutes: 60 },
  { label: "Days", value: "days", multiplierToMinutes: 1440 },
]

export const WORKFLOW_OPERATOR_DEFINITIONS: WorkflowOperatorDefinition[] = [
  operator("equals", "Equals", "The value must exactly match.", ["string", "number", "enum", "duration", "identifier"]),
  operator("does_not_equal", "Does not equal", "The value must not match.", ["string", "number", "enum", "duration", "identifier"]),
  operator("contains", "Contains", "Text must contain the provided value.", ["string"]),
  operator("does_not_contain", "Does not contain", "Text must not contain the provided value.", ["string"]),
  operator("starts_with", "Starts with", "Text must start with the provided value.", ["string"]),
  operator("ends_with", "Ends with", "Text must end with the provided value.", ["string"]),
  operator("greater_than", "Greater than", "The number or duration must be larger than the provided value.", ["number", "duration"]),
  operator("greater_than_or_equal", "Greater than or equal to", "The number or duration must be at least the provided value.", ["number", "duration"]),
  operator("less_than", "Less than", "The number or duration must be smaller than the provided value.", ["number", "duration"]),
  operator("less_than_or_equal", "Less than or equal to", "The number or duration must be at most the provided value.", ["number", "duration"]),
  operator("is_empty", "Is empty", "The value must be blank or unavailable.", ["string", "number", "enum", "duration", "datetime", "date", "identifier"], false),
  operator("is_not_empty", "Is not empty", "The value must be present.", ["string", "number", "enum", "duration", "datetime", "date", "identifier"], false),
  operator("is_yes", "Is yes", "The boolean value must be yes.", ["boolean"], false),
  operator("is_no", "Is no", "The boolean value must be no.", ["boolean"], false),
  operator("before", "Before", "The date or time must be before the provided value.", ["datetime", "date"]),
  operator("after", "After", "The date or time must be after the provided value.", ["datetime", "date"]),
  operator("more_than_minutes_ago", "More than a duration ago", "The date or time must be older than the provided duration.", ["datetime", "date"]),
  operator("less_than_minutes_ago", "Less than a duration ago", "The date or time must be more recent than the provided duration.", ["datetime", "date"]),
  operator("changed_to", "Changed to", "The value changed to the provided value.", ["string", "enum", "identifier"]),
  operator("changed_from", "Changed from", "The value changed from the provided value.", ["string", "enum", "identifier"]),
]

export const WORKFLOW_VARIABLE_DEFINITIONS: WorkflowVariableDefinition[] = [
  variable("lead.id", "Lead ID", "The internal LeadFlow lead identifier.", "Lead", "identifier", ["equals", "does_not_equal", "is_empty", "is_not_empty"], "text", "string", { advanced: true, exampleValues: ["123"] }),
  variable("lead.customer_name", "Customer name", "The customer name shown on the lead.", "Lead", "string", stringOperators(), "text", "string", { exampleValues: ["John Smith"] }),
  variable("lead.stage_key", "Current pipeline stage", "Stable key for the lead's current LeadFlow pipeline stage. Use this for stage comparisons.", "Lead", "enum", ["equals", "does_not_equal", "is_empty", "is_not_empty"], "select", "string", { optionsSource: "pipeline_stages", exampleValues: ["new_lead", "in_progress", "lost"], plainEnglish: "the lead is currently in a specific pipeline stage" }),
  variable("lead.stage_name", "Pipeline stage display name", "Human-readable stage name. Advanced text comparisons only; prefer Current pipeline stage for normal stage rules.", "Lead", "string", stringOperators(), "text", "string", { advanced: true, aliases: ["lead.stage_key"], exampleValues: ["New Lead", "Needs Follow-Up"] }),
  variable("lead.current_stage_age_minutes", "Time in current stage", "The number of minutes since the lead entered its current pipeline stage.", "Lead", "duration", ["equals", "greater_than", "greater_than_or_equal", "less_than", "less_than_or_equal"], "duration", "number", { unitOptions: DURATION_UNIT_OPTIONS, exampleValues: [30, 120, 1440, 4320], plainEnglish: "how long the lead has remained in its current pipeline stage" }),
  variable("lead.source", "Lead source", "Lead source recorded in LeadFlow or FieldRoutes context.", "Lead", "string", stringOperators(), "text", "string", { nullable: true }),
  variable("lead.status", "Lead status", "Lead status text when available.", "Lead", "string", stringOperators(), "text", "string", { nullable: true }),
  variable("lead.assignee_id", "Assigned user", "Internal LeadFlow user assigned to the lead.", "Lead", "identifier", ["equals", "does_not_equal", "is_empty", "is_not_empty"], "select", "string", { optionsSource: "users", nullable: true }),
  variable("lead.created_at", "Lead created time", "When the LeadFlow lead record was created.", "Lead", "datetime", datetimeOperators(), "datetime", "string", { exampleValues: ["2026-07-13T14:30:00.000Z"] }),
  variable("lead.received_at", "Lead received time", "When LeadFlow received or imported the lead.", "Lead", "datetime", datetimeOperators(), "datetime", "string", { exampleValues: ["2026-07-13T14:30:00.000Z"] }),
  variable("lead.last_office_contact_at", "Last office contact", "Most recent known office contact time for this lead.", "Lead", "datetime", datetimeOperators(), "datetime", "string", { nullable: true }),
  variable("lead.potential_value", "Potential value", "Lead potential or contract value when available.", "Lead", "number", numberOperators(), "number", "number", { nullable: true, exampleValues: [649, 1200] }),
  variable("customer.id", "Customer ID", "Internal LeadFlow customer identifier.", "Customer", "identifier", ["equals", "does_not_equal", "is_empty", "is_not_empty"], "text", "string", { advanced: true }),
  variable("customer.name", "Customer name", "Customer display name.", "Customer", "string", stringOperators(), "text", "string"),
  variable("customer.first_name", "Customer first name", "Resolved customer first name for safe email greetings.", "Customer", "string", stringOperators(), "text", "string", { nullable: true }),
  variable("customer.phone", "Phone", "Primary customer phone number.", "Customer", "string", stringOperators(), "text", "string", { nullable: true }),
  variable("customer.email", "Email", "Primary customer email address.", "Customer", "string", stringOperators(), "text", "string", { nullable: true }),
  variable("customer.fieldroutes_customer_id", "FieldRoutes customer ID", "External FieldRoutes customer identifier.", "Customer", "identifier", ["equals", "does_not_equal", "is_empty", "is_not_empty"], "text", "string", { nullable: true }),
  variable("customer.address", "Address", "Customer service address line.", "Customer", "string", stringOperators(), "text", "string", { nullable: true }),
  variable("customer.city", "City", "Customer service city.", "Customer", "string", stringOperators(), "text", "string", { nullable: true }),
  variable("customer.state", "State", "Customer service state.", "Customer", "enum", ["equals", "does_not_equal", "is_empty", "is_not_empty"], "select", "string", { nullable: true, options: stateOptions() }),
  variable("customer.zip", "Zip", "Customer service zip code.", "Customer", "string", stringOperators(), "text", "string", { nullable: true }),
  variable("time.now", "Now", "Current date and time when the workflow is evaluated.", "Time", "datetime", datetimeOperators(), "datetime", "string"),
  variable("time.today", "Today", "Current calendar date when the workflow is evaluated.", "Time", "date", datetimeOperators(), "datetime", "string"),
  variable("time.current_day_of_week", "Current day of week", "Day of week when the workflow is evaluated.", "Time", "enum", ["equals", "does_not_equal"], "select", "string", { options: daysOfWeekOptions() }),
  variable("time.current_hour", "Current hour", "Hour of day when the workflow is evaluated, 0 through 23.", "Time", "number", numberOperators(), "number", "number", { exampleValues: [8, 13, 17] }),
  variable("workflow.id", "Workflow ID", "Internal workflow identifier.", "Workflow", "identifier", ["equals", "does_not_equal"], "text", "string", { advanced: true }),
  variable("workflow.name", "Workflow name", "Current workflow name.", "Workflow", "string", stringOperators(), "text", "string", { advanced: true }),
  variable("workflow.run_id", "Workflow run ID", "Identifier for a future workflow run.", "Workflow", "identifier", ["equals", "does_not_equal", "is_empty", "is_not_empty"], "text", "string", { advanced: true, nullable: true, runtimeAvailable: false }),
  variable("workflow.triggered_at", "Workflow triggered time", "When a future workflow run was triggered.", "Workflow", "datetime", datetimeOperators(), "datetime", "string", { advanced: true, runtimeAvailable: false }),
]

export const WORKFLOW_VARIABLE_OPTIONS = WORKFLOW_VARIABLE_DEFINITIONS.map((definition) => ({
  label: definition.advanced ? `${definition.label} (advanced)` : definition.label,
  value: definition.key,
  kind: kindForVariable(definition),
}))

export const WORKFLOW_OPERATOR_OPTIONS = WORKFLOW_OPERATOR_DEFINITIONS.map((definition) => ({ label: definition.label, value: definition.key }))
export const WORKFLOW_STAGE_VARIABLES = ["lead.stage_key"]
export const WORKFLOW_NUMERIC_VARIABLES = WORKFLOW_VARIABLE_DEFINITIONS.filter((definition) => ["number", "duration"].includes(definition.dataType)).map((definition) => definition.key)

export function workflowVariableByKey(key: string | null | undefined) {
  return WORKFLOW_VARIABLE_DEFINITIONS.find((definition) => definition.key === key)
}

export function workflowOperatorByKey(key: string | null | undefined) {
  return WORKFLOW_OPERATOR_DEFINITIONS.find((definition) => definition.key === key)
}

export function operatorsForVariable(key: string | null | undefined) {
  const variable = workflowVariableByKey(key)
  return (variable ? variable.operators : ["equals", "does_not_equal", "is_empty", "is_not_empty"])
    .map((operatorKey) => workflowOperatorByKey(operatorKey))
    .filter((operator): operator is WorkflowOperatorDefinition => Boolean(operator))
}

export function operatorRequiresValue(operatorKey: string | null | undefined) {
  return workflowOperatorByKey(operatorKey)?.requiresValue ?? true
}

export function conditionValueControlFor(fieldKey: string | null | undefined, operatorKey: string | null | undefined): WorkflowValueControl {
  if (!operatorRequiresValue(operatorKey)) return "none"
  const variable = workflowVariableByKey(fieldKey)
  if (!variable) return "text"
  if (operatorKey === "more_than_minutes_ago" || operatorKey === "less_than_minutes_ago") return "duration"
  return variable.valueControl
}

export function normalizeConditionConfig(config: Record<string, unknown>, stageKeys: string[] = []) {
  const next = { ...config }
  let field = String(next.field ?? "")
  let operatorKey = String(next.operator ?? "")

  if (field === "lead.stage_name" && typeof next.value === "string" && stageKeys.includes(next.value)) {
    field = "lead.stage_key"
    next.field = field
  }

  const operators = operatorsForVariable(field)
  if (!operators.some((operatorDefinition) => operatorDefinition.key === operatorKey)) {
    operatorKey = operators[0]?.key ?? "equals"
    next.operator = operatorKey
  }

  if (!operatorRequiresValue(operatorKey)) {
    next.value = null
    delete next.value_display
    return next
  }

  const variable = workflowVariableByKey(field)
  next.value = normalizeValueForVariable(next.value, variable, operatorKey)
  return next
}

export function normalizeValueForVariable(value: unknown, variable: WorkflowVariableDefinition | undefined, operatorKey?: string) {
  if (operatorKey === "more_than_minutes_ago" || operatorKey === "less_than_minutes_ago") return normalizeNumber(value)
  switch (variable?.jsonValueType) {
    case "number": return normalizeNumber(value)
    case "boolean": return value === true || value === "true"
    case "null": return null
    case "string":
    default:
      return value == null ? "" : String(value)
  }
}

export function durationDisplayFromMinutes(value: unknown) {
  const minutes = normalizeNumber(value)
  if (minutes !== 0 && minutes % 1440 === 0) return { amount: minutes / 1440, unit: "days" }
  if (minutes !== 0 && minutes % 60 === 0) return { amount: minutes / 60, unit: "hours" }
  return { amount: minutes, unit: "minutes" }
}

export function minutesFromDuration(amount: unknown, unit: string) {
  const unitDefinition = DURATION_UNIT_OPTIONS.find((option) => option.value === unit) ?? DURATION_UNIT_OPTIONS[0]
  return normalizeNumber(amount) * (unitDefinition.multiplierToMinutes ?? 1)
}

function operator(key: string, label: string, description: string, dataTypes: WorkflowVariableDataType[], requiresValue = true): WorkflowOperatorDefinition {
  return { key, label, description, dataTypes, requiresValue }
}

function variable(key: string, label: string, description: string, group: string, dataType: WorkflowVariableDataType, operators: string[], valueControl: WorkflowValueControl, jsonValueType: WorkflowJsonValueType, options: Partial<WorkflowVariableDefinition> = {}): WorkflowVariableDefinition {
  return { key, label, description, group, dataType, operators, valueControl, jsonValueType, runtimeAvailable: true, ...options }
}

function stringOperators() {
  return ["equals", "does_not_equal", "contains", "does_not_contain", "starts_with", "ends_with", "is_empty", "is_not_empty"]
}

function numberOperators() {
  return ["equals", "does_not_equal", "greater_than", "greater_than_or_equal", "less_than", "less_than_or_equal", "is_empty", "is_not_empty"]
}

function datetimeOperators() {
  return ["is_empty", "is_not_empty", "before", "after", "more_than_minutes_ago", "less_than_minutes_ago"]
}

function kindForVariable(definition: WorkflowVariableDefinition): "string" | "number" | "boolean" | "select" | "stage_select" | "user_select" | "duration" {
  if (definition.optionsSource === "pipeline_stages") return "stage_select"
  if (definition.optionsSource === "users") return "user_select"
  if (definition.valueControl === "duration") return "duration"
  if (definition.valueControl === "boolean") return "boolean"
  if (definition.valueControl === "number") return "number"
  if (definition.valueControl === "select") return "select"
  return "string"
}

function normalizeNumber(value: unknown) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function stateOptions() {
  return ["MO", "KS", "AR", "OK"].map((value) => ({ label: value, value }))
}

function daysOfWeekOptions() {
  return ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((label) => ({ label, value: label.toLowerCase() }))
}
